/**
 * ACMLink — správa spárovaných prohlížečů/rozšíření.
 *
 * Core relay modul: rozšíření zachytává loopback OAuth callbacky (localhost),
 * co ACM samo nemůže obsloužit (viz modules/acm-link). Tahle stránka jen
 * spravuje pairing klíče — spuštění konkrétních OAuth flow (např. Codable/Codex)
 * žije v tom modulu, co ho potřebuje.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Bot,
  Check,
  Copy,
  Download,
  KeyRound,
  Plus,
  Puzzle,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { api } from '../../lib/api';
import {
  type BrowserInfo,
  type CompanionResponse,
  currentAcmHost,
  detectBrowser,
  type ExtensionStatus,
  installAgentAccess,
  stationProof,
  stationCSR,
  installStation,
  pingExtension,
  revokeAgentAccess,
  sendPrefillToExtension,
} from '../../lib/acm-link-client';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  SectionHeader,
} from '../../components/ui';
import { themedConfirm } from '../../lib/themed-dialogs';

interface Pairing {
  id: string;
  key_preview: string;
  label: string | null;
  created_at: string;
  last_seen_at: string | null;
  enabled: boolean;
}

interface CodableProject {
  slug: string;
  name?: string | null;
  orchestrator_project_id?: string | null;
}

type AgentAutonomy = 'observe' | 'develop' | 'operate';

interface AgentGrant {
  access_kind?: string;
  agent_id: string;
  agent_label: string;
  device_label: string | null;
  client_type: string;
  agent_enabled: boolean;
  grant_id: string;
  project_slug: string;
  project_id: string;
  autonomy: AgentAutonomy;
  policy: Record<string, 'allow' | 'confirm' | 'deny'>;
  grant_enabled: boolean;
  expires_at: string | null;
  created_at: string;
  revoked_at: string | null;
  key_id: string;
  key_preview: string;
  key_enabled: boolean;
  last_used: string | null;
  use_count: number;
}

interface AgentInstallBundle {
  access_kind?: string;
  version: number;
  grant_id: string;
  agent_id: string;
  agent_label: string;
  device_label: string | null;
  project_slug: string;
  project_id: string;
  autonomy: AgentAutonomy;
  policy: Record<string, 'allow' | 'confirm' | 'deny'>;
  mcp_url: string;
  api_key: string;
  expires_at: string | null;
}

export default function AcmLinkPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [browser] = useState<BrowserInfo>(() => detectBrowser());
  const [extensionStatus, setExtensionStatus] = useState<ExtensionStatus>('checking');
  const [label, setLabel] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [showAgentCreate, setShowAgentCreate] = useState(false);
  const [agentLabel, setAgentLabel] = useState('');
  const [deviceLabel, setDeviceLabel] = useState('');
  const [projectSlug, setProjectSlug] = useState('');
  const [autonomy, setAutonomy] = useState<AgentAutonomy>('develop');
  const [expiresDays, setExpiresDays] = useState('30');
  const [newAgentBundle, setNewAgentBundle] = useState<AgentInstallBundle | null>(null);
  const [companionResult, setCompanionResult] = useState<CompanionResponse | null>(null);

  const displayBrowser = browser.name === 'unknown' ? t('acmLink.unknownBrowser') : browser.name;
  const osLabel = browser.os === 'windows'
    ? t('acmLink.os.windows')
    : browser.os === 'linux'
    ? t('acmLink.os.linux')
    : browser.os === 'macos'
    ? t('acmLink.os.macos')
    : t('acmLink.os.unknown');

  /** Primární balíček podle detekovaného OS — stahuje se přes MCP acm-link.package_get. */
  const primaryDownload = ((): { target: string; label: string; hint: string } => {
    if (browser.os === 'windows') {
      return {
        target: 'windows',
        label: t('acmLink.download.windows'),
        hint: t('acmLink.download.windowsHint'),
      };
    }
    if (browser.os === 'linux') {
      return {
        target: 'linux',
        label: t('acmLink.download.linux'),
        hint: t('acmLink.download.linuxHint'),
      };
    }
    if (browser.os === 'macos') {
      return {
        target: 'macos',
        label: t('acmLink.download.macos'),
        hint: t('acmLink.download.macosHint'),
      };
    }
    return {
      target: 'extension',
      label: t('acmLink.downloadExtension'),
      hint: t('acmLink.download.genericHint'),
    };
  })();

  const otherDownloads = (
    [
      { target: 'windows', label: t('acmLink.download.windowsShort') },
      { target: 'linux', label: t('acmLink.download.linuxShort') },
      { target: 'macos', label: t('acmLink.download.macosShort') },
      { target: 'extension', label: t('acmLink.download.extensionShort') },
    ] as const
  ).filter((d) => d.target !== primaryDownload.target);

  const installSteps = (() => {
    const step2 = (
      <span key='step2'>
        {t('acmLink.install.step2')}{' '}
        <button
          type='button'
          onClick={() => copy(browser.extensionsUrl)}
          className='inline-flex items-center gap-1 rounded bg-gray-800 px-1.5 py-0.5 font-mono text-gray-200 hover:bg-gray-700'
          title={t('acmLink.install.copyUrlTitle')}
        >
          {browser.extensionsUrl} <Copy size={10} />
        </button>{' '}
        {t('acmLink.install.step2Hint')}
      </span>
    );
    if (browser.os === 'windows') {
      return [
        t('acmLink.install.windows.step1'),
        t('acmLink.install.windows.step2'),
        step2,
        t('acmLink.install.windows.step4'),
        t('acmLink.install.step5'),
      ];
    }
    if (browser.os === 'linux') {
      return [
        t('acmLink.install.linux.step1'),
        t('acmLink.install.linux.step2'),
        step2,
        t('acmLink.install.linux.step4'),
        t('acmLink.install.step5'),
      ];
    }
    // macOS / unknown — klasický zip + Load unpacked
    return [
      t('acmLink.install.step1'),
      step2,
      t('acmLink.install.step3'),
      t('acmLink.install.step4'),
      t('acmLink.install.step5'),
    ];
  })();

  useEffect(() => {
    if (!browser.chromiumBased) {
      // Rozšíření je Manifest V3 pro Chromium — na Firefoxu/Safari ho nemá cenu
      // ani hledat (nikdy tam nebude nainstalované, dokud pro ně nepostavíme port).
      setExtensionStatus('unsupported');
      return;
    }
    let cancelled = false;
    pingExtension().then((found) => {
      if (!cancelled) setExtensionStatus(found ? 'found' : 'missing');
    });
    return () => {
      cancelled = true;
    };
  }, [browser.chromiumBased]);

  const pairingsQ = useQuery({
    queryKey: ['acm-link-pairings'],
    queryFn: () => api.mcp<{ ok: boolean; pairings: Pairing[] }>('acm-link.pair_list', {}),
  });

  const projectsQ = useQuery({
    queryKey: ['acm-link-codable-projects'],
    queryFn: () =>
      api.mcp<{ ok?: boolean; projects?: CodableProject[] }>('codable.project_list', {}),
  });

  const agentGrantsQ = useQuery({
    queryKey: ['acm-link-agent-grants'],
    queryFn: () => api.mcp<{ ok: boolean; agents: AgentGrant[] }>('acm-link.agent_grant_list', {}),
  });

  const createM = useMutation({
    mutationFn: () =>
      api.mcp<{ ok: boolean; key?: string; error?: string }>('acm-link.pair_create', {
        label: label.trim() || undefined,
      }),
    onSuccess: (r) => {
      if (!r.ok || !r.key) {
        toast.error(r.error ?? t('acmLink.toast.createError'));
        return;
      }
      setNewKey(r.key);
      setLabel('');
      qc.invalidateQueries({ queryKey: ['acm-link-pairings'] });
      if (extensionStatus === 'found') {
        sendPrefillToExtension(r.key);
        toast.success(t('acmLink.toast.prefillOpen'));
      }
    },
    onError: (e: unknown) => toast.error(String((e as Error).message)),
  });

  const revokeM = useMutation({
    mutationFn: (id: string) =>
      api.mcp<{ ok: boolean; error?: string }>('acm-link.pair_revoke', { id }),
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? t('acmLink.toast.revokeError'));
        return;
      }
      toast.success(t('acmLink.toast.revoked'));
      qc.invalidateQueries({ queryKey: ['acm-link-pairings'] });
    },
  });

  const [stationBackground, setStationBackground] = useState(false);
  const [stationMaxMinutes, setStationMaxMinutes] = useState(480);
  const createAgentGrantM = useMutation({
    mutationFn: async () => {
      const local = await stationProof();
      if (!local.ok || !local.proof) throw new Error(local.error || 'acmlink_companion_unavailable');
      const bound = await api.mcp<{ ok: boolean; error?: string; station?: Record<string, unknown> }>(
        'acm-link.station_bind', { proof: local.proof, label: deviceLabel.trim(), background_execution: stationBackground, max_session_minutes: stationMaxMinutes });
      if (!bound.ok || !bound.station) throw new Error(bound.error || 'station_registration_failed');
      const installed = await installStation(bound.station);
      if (!installed.ok) throw new Error(installed.error || 'station_install_failed');
      if (projectSlug === '__operator__') {
        const csr = await stationCSR();
        if (!csr.ok || !csr.csr_pem) throw new Error(csr.error || 'station_csr_failed');
        return api.mcp<{ ok: boolean; error?: string; install_bundle?: AgentInstallBundle }>(
          'acm-link.operator_access_create', { pairing_id: bound.station.pairing_id,
            csr_pem: csr.csr_pem, label: agentLabel.trim(), autonomy });
      }
      return api.mcp<{
        ok: boolean;
        error?: string;
        install_bundle?: AgentInstallBundle;
      }>('acm-link.agent_grant_create', {
        pairing_id: bound.station.pairing_id,
        agent_label: agentLabel.trim(),
        device_label: deviceLabel.trim() || undefined,
        client_type: 'generic',
        project_slug: projectSlug,
        autonomy,
        expires_days: expiresDays ? Number(expiresDays) : undefined,
      });
    },
    onSuccess: async (r) => {
      if (!r.ok || !r.install_bundle) {
        toast.error(r.error ?? t('acmLink.agentAccess.toast.createError'));
        return;
      }
      setNewAgentBundle(r.install_bundle);
      setCompanionResult(null);
      qc.invalidateQueries({ queryKey: ['acm-link-agent-grants'] });
      if (extensionStatus === 'found') {
        const installed = await installAgentAccess(
          r.install_bundle as unknown as Record<string, unknown>,
        );
        setCompanionResult(installed);
        if (installed.ok) {
          // Plaintext po úspěšném předání nenecháváme dál v React state/DOM.
          setNewAgentBundle({ ...r.install_bundle, api_key: '' });
          toast.success(t('acmLink.agentAccess.toast.installed'));
          return;
        }
      }
      toast.success(t('acmLink.agentAccess.toast.created'));
    },
    onError: (e: unknown) => toast.error(String((e as Error).message)),
  });

  const revokeAgentGrantM = useMutation({
    mutationFn: (grantId: string) =>
      (newAgentBundle?.grant_id === grantId && newAgentBundle.access_kind === 'operator') ||
      agentGrantsQ.data?.agents.some((g) => g.grant_id === grantId && g.access_kind === 'operator')
        ? api.mcp<{ ok: boolean; error?: string }>('acm-self.connect', { action: 'revoke', key_id: grantId })
        : api.mcp<{ ok: boolean; error?: string }>('acm-link.agent_grant_revoke', { grant_id: grantId }),
    onSuccess: (r, grantId) => {
      if (!r.ok) {
        toast.error(r.error ?? t('acmLink.agentAccess.toast.revokeError'));
        return;
      }
      toast.success(t('acmLink.agentAccess.toast.revoked'));
      qc.invalidateQueries({ queryKey: ['acm-link-agent-grants'] });
      if (newAgentBundle?.grant_id === grantId) setNewAgentBundle(null);
      void revokeAgentAccess(grantId);
    },
  });

  /** MCP → base64 zip → browser download (žádné /apiext). */
  const downloadM = useMutation({
    mutationFn: (target: string) =>
      api.mcp<{
        ok: boolean;
        error?: string;
        filename?: string;
        content_type?: string;
        base64?: string;
        size_bytes?: number;
      }>('acm-link.package_get', { target }, { label: t('acmLink.download.loading') }),
    onSuccess: (r) => {
      if (!r.ok || !r.base64 || !r.filename) {
        toast.error(r.error ?? t('acmLink.download.fail'));
        return;
      }
      try {
        const bin = atob(r.base64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const blob = new Blob([bytes], { type: r.content_type ?? 'application/zip' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = r.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success(t('acmLink.download.ok', { name: r.filename }));
      } catch (e) {
        toast.error(t('acmLink.download.fail') + ': ' + String((e as Error).message ?? e));
      }
    },
    onError: (e: unknown) => toast.error(String((e as Error).message)),
  });

  const copy = (v: string) => {
    navigator.clipboard.writeText(v).then(() => toast.success(t('acmLink.toast.copied'))).catch(
      () => toast.error(t('acmLink.toast.copyFailed')),
    );
  };

  const pairings = pairingsQ.data?.pairings ?? [];
  const projects = projectsQ.data?.projects ?? [];
  const agentGrants = agentGrantsQ.data?.agents ?? [];

  return (
    <div className='space-y-5'>
      <SectionHeader
        title={t('acmLink.title')}
        description={t('acmLink.description')}
        actions={
          <Button
            variant='primary'
            size='sm'
            icon={<Plus size={14} />}
            onClick={() => setShowCreate(true)}
          >
            {t('acmLink.pairBrowser')}
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <div className='flex items-center justify-between gap-3'>
            <div>
              <CardTitle>{t('acmLink.agentAccess.title')}</CardTitle>
              <p className='mt-1 text-xs text-gray-500'>{t('acmLink.agentAccess.description')}</p>
            </div>
            <Button
              variant='primary'
              size='sm'
              icon={<Bot size={14} />}
              onClick={() => {
                setShowAgentCreate(true);
                setNewAgentBundle(null);
                setCompanionResult(null);
                if (!projectSlug && projects[0]?.slug) setProjectSlug(projects[0].slug);
              }}
            >
              {t('acmLink.agentAccess.add')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className='space-y-3'>
          <div className='rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 text-xs text-gray-400'>
            <div className='flex items-center gap-2 font-medium text-blue-300'>
              <ShieldCheck size={15} /> {t('acmLink.agentAccess.simpleRuleTitle')}
            </div>
            <p className='mt-1'>{t('acmLink.agentAccess.simpleRule')}</p>
            <p className='mt-1 text-gray-500'>{t('acmLink.agentAccess.approvalFoundation')}</p>
          </div>

          {showAgentCreate && (
            <div className='rounded-xl border border-gray-800 bg-gray-950 p-4 space-y-3'>
              {!newAgentBundle
                ? (
                  <>
                    <div className='grid gap-3 md:grid-cols-2'>
                      <label className='space-y-1 text-xs text-gray-400'>
                        <span>{t('acmLink.agentAccess.agentLabel')}</span>
                        <input
                          value={agentLabel}
                          onChange={(e) => setAgentLabel(e.target.value)}
                          placeholder={t('acmLink.agentAccess.agentPlaceholder')}
                          className='w-full rounded border border-gray-800 bg-gray-900 px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none'
                        />
                      </label>
                      <label className='space-y-1 text-xs text-gray-400'>
                        <span>{t('acmLink.agentAccess.deviceLabel')}</span>
                        <input
                          value={deviceLabel}
                          onChange={(e) => setDeviceLabel(e.target.value)}
                          placeholder={t('acmLink.agentAccess.devicePlaceholder')}
                          className='w-full rounded border border-gray-800 bg-gray-900 px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none'
                        />
                      </label>
                      <label className='space-y-1 text-xs text-gray-400'>
                        <span>{t('acmLink.agentAccess.project')}</span>
                        <select
                          value={projectSlug}
                          onChange={(e) => setProjectSlug(e.target.value)}
                          className='w-full rounded border border-gray-800 bg-gray-900 px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none'
                        >
                          <option value=''>{t('acmLink.agentAccess.chooseProject')}</option>
                          <option value='__operator__'>{t('acmLink.operatorStation.wholeAcm')}</option>
                          {projects.map((project) => (
                            <option key={project.slug} value={project.slug}>
                              {project.name || project.slug}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className='space-y-1 text-xs text-gray-400'>
                        <span>{t('acmLink.operatorStation.maxSession')}</span>
                        <input type='number' min={15} max={1440} value={stationMaxMinutes}
                          onChange={(e) => setStationMaxMinutes(Number(e.target.value))}
                          className='w-full rounded border border-gray-800 bg-gray-900 px-2 py-1.5' />
                      </label>
                      <label className='space-x-2 text-xs text-gray-400'>
                        <input type='checkbox' checked={stationBackground}
                          onChange={(e) => setStationBackground(e.target.checked)} />
                        <span>{t('acmLink.operatorStation.background')}</span>
                      </label>
                      <label className='space-y-1 text-xs text-gray-400'>
                        <span>{t('acmLink.agentAccess.autonomy')}</span>
                        <select
                          value={autonomy}
                          onChange={(e) => setAutonomy(e.target.value as AgentAutonomy)}
                          className='w-full rounded border border-gray-800 bg-gray-900 px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none'
                        >
                          <option value='observe'>{t('acmLink.agentAccess.modes.observe')}</option>
                          <option value='develop'>{t('acmLink.agentAccess.modes.develop')}</option>
                          <option value='operate'>{t('acmLink.agentAccess.modes.operate')}</option>
                        </select>
                      </label>
                      <label className='space-y-1 text-xs text-gray-400'>
                        <span>{t('acmLink.agentAccess.expiry')}</span>
                        <input
                          type='number'
                          min='1'
                          max='365'
                          value={expiresDays}
                          onChange={(e) => setExpiresDays(e.target.value)}
                          className='w-full rounded border border-gray-800 bg-gray-900 px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none'
                        />
                      </label>
                    </div>
                    <p className='text-[11px] text-gray-500'>
                      {t(`acmLink.agentAccess.modeHints.${autonomy}`)}
                    </p>
                    <div className='flex gap-2'>
                      <Button
                        variant='primary'
                        size='sm'
                        loading={createAgentGrantM.isPending}
                        disabled={!agentLabel.trim() || !projectSlug}
                        onClick={() => createAgentGrantM.mutate()}
                      >
                        {t('acmLink.agentAccess.create')}
                      </Button>
                      <Button variant='ghost' size='sm' onClick={() => setShowAgentCreate(false)}>
                        {t('acmLink.cancel')}
                      </Button>
                    </div>
                  </>
                )
                : (
                  <div className='space-y-3'>
                    <div className='flex items-center gap-2 text-sm font-medium text-emerald-300'>
                      <Check size={16} /> {t('acmLink.agentAccess.createdTitle')}
                    </div>
                    <p className='text-xs text-gray-400'>
                      {t('acmLink.agentAccess.createdDescription')}
                    </p>
                    <div className='grid gap-2 text-xs md:grid-cols-2'>
                      <div className='rounded border border-gray-800 p-2'>
                        <span className='text-gray-500'>{t('acmLink.agentAccess.agentLabel')}</span>
                        <div className='text-gray-200'>{newAgentBundle.agent_label}</div>
                      </div>
                      <div className='rounded border border-gray-800 p-2'>
                        <span className='text-gray-500'>{t('acmLink.agentAccess.project')}</span>
                        <div className='text-gray-200'>{newAgentBundle.access_kind === 'operator' ? t('acmLink.operatorStation.wholeAcm') : newAgentBundle.project_slug}</div>
                      </div>
                    </div>
                    {companionResult?.ok && companionResult.local_mcp_url
                      ? (
                        <div className='rounded border border-emerald-500/30 bg-emerald-500/5 p-3'>
                          <div className='flex items-center gap-2 text-xs font-medium text-emerald-300'>
                            <ShieldCheck size={14} /> {t('acmLink.agentAccess.installedTitle')}
                          </div>
                          <p className='mt-1 text-[11px] text-gray-500'>
                            {t('acmLink.agentAccess.installedHint')}
                          </p>
                          <div className='mt-2 flex items-center gap-2'>
                            <code className='min-w-0 flex-1 break-all rounded bg-gray-950 px-2 py-1.5 text-[11px] text-emerald-200'>
                              {companionResult.local_mcp_url}
                            </code>
                            <Button
                              size='sm'
                              variant='ghost'
                              icon={<Copy size={14} />}
                              onClick={() => copy(companionResult.local_mcp_url!)}
                            />
                          </div>
                        </div>
                      )
                      : (
                        <div className='rounded border border-amber-500/30 bg-amber-500/5 p-3'>
                          <div className='flex items-center gap-2 text-xs font-medium text-amber-300'>
                            <KeyRound size={14} /> {t('acmLink.agentAccess.oneTimeKey')}
                          </div>
                          <p className='mt-2 text-[11px] text-gray-500'>
                            {companionResult?.error
                              ? t('acmLink.agentAccess.companionUnavailable')
                              : t('acmLink.agentAccess.oneTimeHint')}
                          </p>
                          <div className='mt-3 flex flex-wrap gap-2'>
                            <Button
                              size='sm'
                              variant='secondary'
                              disabled={extensionStatus !== 'found'}
                              onClick={async () => {
                                const installed = await installAgentAccess(
                                  newAgentBundle as unknown as Record<string, unknown>,
                                );
                                setCompanionResult(installed);
                                if (installed.ok) {
                                  setNewAgentBundle({ ...newAgentBundle, api_key: '' });
                                  toast.success(t('acmLink.agentAccess.toast.installed'));
                                }
                              }}
                            >
                              {t('acmLink.agentAccess.retryHandoff')}
                            </Button>
                            <Button
                              size='sm'
                              variant='ghost'
                              onClick={() => revokeAgentGrantM.mutate(newAgentBundle.grant_id)}
                            >
                              {t('acmLink.agentAccess.revokeUnused')}
                            </Button>
                          </div>
                        </div>
                      )}
                    <div className='flex justify-end'>
                      <Button
                        size='sm'
                        variant='secondary'
                        onClick={() => {
                          setNewAgentBundle(null);
                          setShowAgentCreate(false);
                          setAgentLabel('');
                          setDeviceLabel('');
                        }}
                      >
                        {t('acmLink.done')}
                      </Button>
                    </div>
                  </div>
                )}
            </div>
          )}

          {agentGrantsQ.isLoading && (
            <div className='text-sm text-gray-500'>{t('acmLink.loading')}</div>
          )}
          {!agentGrantsQ.isLoading && agentGrants.length === 0 && (
            <EmptyState
              icon={<Bot size={24} />}
              title={t('acmLink.agentAccess.emptyTitle')}
              description={t('acmLink.agentAccess.emptyDescription')}
            />
          )}
          <div className='space-y-2'>
            {agentGrants.map((grant) => {
              const active = grant.agent_enabled && grant.grant_enabled && grant.key_enabled;
              return (
                <div
                  key={grant.grant_id}
                  className={`rounded-lg border p-3 ${
                    active
                      ? 'border-gray-800 bg-gray-950'
                      : 'border-gray-800 bg-gray-950/50 opacity-60'
                  }`}
                >
                  <div className='flex items-start gap-3'>
                    <Bot size={17} className='mt-0.5 text-blue-400' />
                    <div className='min-w-0 flex-1'>
                      <div className='flex flex-wrap items-center gap-2'>
                        <span className='font-medium text-gray-200'>{grant.agent_label}</span>
                        <Badge variant={active ? 'green' : 'gray'} dot>
                          {active
                            ? t('acmLink.agentAccess.active')
                            : t('acmLink.agentAccess.revoked')}
                        </Badge>
                        <Badge variant='blue'>
                          {t(`acmLink.agentAccess.modes.${grant.autonomy}`)}
                        </Badge>
                      </div>
                      <div className='mt-1 text-xs text-gray-400'>
                        {grant.project_slug} · {grant.project_id}
                        {grant.device_label ? ` · ${grant.device_label}` : ''}
                      </div>
                      <div className='mt-1 text-[11px] text-gray-600 font-mono'>
                        {grant.key_preview}… ·{' '}
                        {t('acmLink.agentAccess.used', { count: grant.use_count ?? 0 })}
                        {grant.last_used ? ` · ${new Date(grant.last_used).toLocaleString()}` : ''}
                      </div>
                    </div>
                    {active && (
                      <button
                        className='text-gray-500 hover:text-red-400'
                        title={t('acmLink.agentAccess.revoke')}
                        onClick={async () => {
                          if (
                            await themedConfirm(t('acmLink.agentAccess.revokeConfirm', {
                              agent: grant.agent_label,
                              project: grant.project_slug,
                            }))
                          ) revokeAgentGrantM.mutate(grant.grant_id);
                        }}
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            {t('acmLink.extensionInBrowser', { browser: displayBrowser })}
            <span className='ml-2 text-xs font-normal text-gray-500'>· {osLabel}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className='flex flex-wrap items-center gap-2'>
            <Badge
              variant={extensionStatus === 'found'
                ? 'green'
                : extensionStatus === 'missing'
                ? 'red'
                : extensionStatus === 'unsupported'
                ? 'gray'
                : 'gray'}
              dot
            >
              {extensionStatus === 'checking'
                ? t('acmLink.statusChecking')
                : extensionStatus === 'found'
                ? t('acmLink.statusFound')
                : extensionStatus === 'unsupported'
                ? t('acmLink.statusUnsupported')
                : t('acmLink.statusMissing')}
            </Badge>
          </div>
          {(extensionStatus === 'missing' || extensionStatus === 'found') &&
            browser.chromiumBased && (
            <div className='mt-3 space-y-3'>
              <div className='rounded-xl border border-gray-800 bg-gray-900/40 p-3 space-y-2'>
                <div className='text-[10px] font-semibold uppercase tracking-widest text-gray-600'>
                  {t('acmLink.download.forThisMachine', { browser: displayBrowser, os: osLabel })}
                </div>
                <Button
                  variant='primary'
                  size='sm'
                  icon={<Download size={15} />}
                  loading={downloadM.isPending && downloadM.variables === primaryDownload.target}
                  disabled={downloadM.isPending}
                  onClick={() => downloadM.mutate(primaryDownload.target)}
                >
                  {primaryDownload.label}
                </Button>
                <p className='text-[11px] text-gray-500 leading-relaxed'>{primaryDownload.hint}</p>
                <div className='flex flex-wrap items-center gap-x-3 gap-y-1 pt-1 text-[11px] text-gray-500'>
                  <span>{t('acmLink.download.otherPlatforms')}</span>
                  {otherDownloads.map((d) => (
                    <button
                      key={d.target}
                      type='button'
                      disabled={downloadM.isPending}
                      onClick={() => downloadM.mutate(d.target)}
                      className='text-blue-400/90 hover:text-blue-300 underline-offset-2 hover:underline disabled:opacity-50'
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
              {extensionStatus === 'missing' && (
                <>
                  <p className='text-[11px] text-gray-500 leading-relaxed'>
                    {browser.os === 'windows'
                      ? t('acmLink.install.windows.intro')
                      : browser.os === 'linux'
                      ? t('acmLink.install.linux.intro')
                      : t('acmLink.install.genericIntro')}
                  </p>
                  <ol className='space-y-2.5'>
                    {installSteps.map((step, i) => (
                      <li key={i} className='flex gap-2.5 text-xs text-gray-400'>
                        <span className='flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-gray-800 text-[10px] font-semibold text-gray-300'>
                          {i + 1}
                        </span>
                        <span className='leading-relaxed'>{step}</span>
                      </li>
                    ))}
                  </ol>
                </>
              )}
            </div>
          )}
          {extensionStatus === 'unsupported' && (
            <div className='mt-2 text-xs text-gray-500 leading-relaxed'>
              {t('acmLink.unsupported', { browser: displayBrowser })}
            </div>
          )}
        </CardContent>
      </Card>

      {showCreate && (
        <Card>
          <CardHeader>
            <CardTitle>{t('acmLink.pairNew')}</CardTitle>
          </CardHeader>
          <CardContent className='space-y-3'>
            {!newKey
              ? (
                <>
                  <input
                    className='w-full bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none'
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder={t('acmLink.labelPlaceholder')}
                  />
                  <div className='flex gap-2'>
                    <Button
                      variant='primary'
                      size='sm'
                      loading={createM.isPending}
                      onClick={() => createM.mutate()}
                    >
                      {t('acmLink.createKey')}
                    </Button>
                    <Button variant='ghost' size='sm' onClick={() => setShowCreate(false)}>
                      {t('acmLink.cancel')}
                    </Button>
                  </div>
                </>
              )
              : (
                <div className='border border-emerald-500/40 bg-emerald-500/5 rounded-lg p-4 space-y-3'>
                  <div className='flex items-center gap-2 text-emerald-300 font-medium text-sm'>
                    <Check size={16} /> {t('acmLink.keyCreated')}
                  </div>
                  <div className='space-y-1'>
                    <div className='text-[10px] uppercase tracking-wider text-gray-600'>
                      {t('acmLink.acmHostLabel', 'ACM host (tato adresa)')}
                    </div>
                    <div className='flex items-center gap-2'>
                      <code className='flex-1 bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-xs text-blue-300 font-mono break-all'>
                        {currentAcmHost()}
                      </code>
                      <Button
                        size='sm'
                        variant='ghost'
                        icon={<Copy size={14} />}
                        onClick={() => copy(currentAcmHost())}
                      />
                    </div>
                    <p className='text-[11px] text-gray-500'>
                      {t(
                        'acmLink.acmHostHint',
                        'Odráží adresu v prohlížeči (veřejná doména i LAN) — ne interní IP ze serveru.',
                      )}
                    </p>
                  </div>
                  <div className='space-y-1'>
                    <div className='text-[10px] uppercase tracking-wider text-gray-600'>
                      {t('acmLink.pairingKeyLabel', 'Párovací klíč')}
                    </div>
                    <div className='flex items-center gap-2'>
                      <code className='flex-1 bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-xs text-emerald-300 font-mono break-all'>
                        {newKey}
                      </code>
                      <Button
                        size='sm'
                        variant='ghost'
                        icon={<Copy size={14} />}
                        onClick={() => copy(newKey)}
                      />
                    </div>
                  </div>
                  {extensionStatus === 'found' && (
                    <div className='text-xs text-gray-400 space-y-2'>
                      <p>{t('acmLink.extensionFoundHint')}</p>
                      <Button
                        size='sm'
                        variant='secondary'
                        onClick={() => {
                          sendPrefillToExtension(newKey);
                          toast.success(t('acmLink.toast.prefillOpen'));
                        }}
                      >
                        {t('acmLink.reopenPrefill', 'Znovu otevřít nastavení rozšíření')}
                      </Button>
                    </div>
                  )}
                  {extensionStatus === 'missing' && (
                    <div className='text-xs text-gray-400'>
                      {t('acmLink.extensionMissingHint')}
                    </div>
                  )}
                  {extensionStatus === 'unsupported' && (
                    <div className='text-xs text-gray-400'>
                      {t('acmLink.extensionUnsupportedHint', { browser: displayBrowser })}
                    </div>
                  )}
                  <div className='flex justify-end'>
                    <Button
                      size='sm'
                      variant='secondary'
                      onClick={() => {
                        setNewKey(null);
                        setShowCreate(false);
                      }}
                    >
                      {t('acmLink.done')}
                    </Button>
                  </div>
                </div>
              )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('acmLink.pairedBrowsers')}</CardTitle>
        </CardHeader>
        <CardContent>
          {pairingsQ.isLoading && (
            <div className='text-gray-500 text-sm'>{t('acmLink.loading')}</div>
          )}
          {!pairingsQ.isLoading && pairings.length === 0 && (
            <EmptyState
              icon={<Puzzle size={24} />}
              title={t('acmLink.noPairingsTitle')}
              description={t('acmLink.noPairingsDesc')}
            />
          )}
          <div className='space-y-1.5'>
            {pairings.map((p) => (
              <div
                key={p.id}
                className={`border rounded-lg p-2.5 flex items-center gap-2 text-sm ${
                  p.enabled
                    ? 'border-gray-800 bg-gray-950'
                    : 'border-gray-800 bg-gray-950/50 opacity-60'
                }`}
              >
                <div className='flex-1 min-w-0'>
                  <div className='text-gray-200 truncate'>{p.label ?? t('acmLink.noLabel')}</div>
                  <div className='text-[11px] text-gray-500 font-mono'>
                    {p.key_preview}… · {t('acmLink.created')}{' '}
                    {new Date(p.created_at).toLocaleDateString()}
                    {p.last_seen_at
                      ? ` · ${t('acmLink.lastSeen')} ${new Date(p.last_seen_at).toLocaleString()}`
                      : ` · ${t('acmLink.neverUsed')}`}
                  </div>
                </div>
                <button
                  onClick={async () => {
                    if (
                      await themedConfirm(
                        t('acmLink.revokeConfirm', { label: p.label ?? p.key_preview }),
                      )
                    ) revokeM.mutate(p.id);
                  }}
                  className='text-gray-500 hover:text-red-400'
                  title={t('acmLink.revokeTitle')}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
